/**
 * Two camera modes over one PerspectiveCamera:
 * - orbit: damped OrbitControls, auto-framed to the track's bounds.
 * - follow: chases a point 4 units behind the marble along the centerline.
 */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { SceneCtx } from "./scene.ts";
import type { Marble } from "./marble.ts";

export type CamMode = "orbit" | "follow";
type CameraSubject = Pick<Marble, "obj" | "s" | "speed" | "radius" | "update">;

export interface Cameras {
  readonly mode: CamMode;
  update(dt: number): void;
  toggle(): CamMode;
  setMode(mode: CamMode): void;
  /** Point at a new curve/marble pair after a regeneration. */
  retarget(
    curve: THREE.Curve<THREE.Vector3> | null,
    marble: CameraSubject | null,
  ): void;
  /**
   * Endless mode: follow via an arc-length sampler instead of one closed
   * curve (the path grows without bound). `getPointAtS` writes the world
   * point at global s into `out` and returns false outside the built window.
   */
  retargetPath(
    getPointAtS: ((s: number, out: THREE.Vector3) => boolean) | null,
    marble: CameraSubject | null,
  ): void;
  /** Re-aim orbit at a new bounding box. */
  frame(box: THREE.Box3): void;
  onModeChange?: (mode: CamMode) => void;
  dispose(): void;
}

/** How far behind the marble (in curve units) the follow camera rides. */
const TRAIL = 4;
const HEIGHT = 1.6;
const LERP = 0.08;
const LOOK_UP = 0.5;
/** Follow-mode drag angles ease back after quiet seconds. Wheel zoom
 * stays at the user's chosen distance until they change it again. */
const ADJUST_RETURN_AFTER = 3;
const ADJUST_RETURN_RATE = 2.2; // exp decay per second
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 3;
/** Occlusion pull-in (follow mode). CLIP_MARGIN is how far in front of a
 * hit surface the boom settles; NEAR_CLEARANCE is the hard floor between
 * lens and surface — it must exceed the camera near plane (0.1 in
 * scene.ts) at the frustum CORNERS (near/cos of the diagonal half-fov),
 * or the near plane slices the wall even though the camera origin is
 * outside it. MIN_BOOM keeps the shot from collapsing onto the marble
 * when it hugs a wall — but never wins over staying outside the mass. */
const CLIP_MARGIN = 0.35;
const NEAR_CLEARANCE = 0.2;
const MIN_BOOM = 0.8;
/** Boom release: snap IN when a mass intrudes (clipping is worse than a
 * pop), ease back OUT at this exp rate per second once it clears. */
const BOOM_RELEASE_RATE = 3;
/** Look-target discontinuity that reads as a TELEPORT (respawns move the
 * subject tens of units): the camera cuts to the fresh framing. */
const TELEPORT_CUT = 5;
/** Thin lips, arches and isolated track walls may briefly cross the view
 * without pulling the camera all the way back to the marble. */
const THIN_SPAN = 1.6;

/** Find a solid obstruction, measuring entry/exit on the SAME mesh and
 * instance. Pairing hits from different pieces counts the empty space
 * between them as solid and causes large, unnecessary camera dives.
 * Wide views use lensOnly: passing scenery may cover the subject briefly,
 * but only a solid containing the lens endpoint should shorten the boom. */
export function cameraObstructionHit(
  raycaster: THREE.Raycaster,
  origin: THREE.Vector3,
  dir: THREE.Vector3,
  far: number,
  objects: readonly THREE.Object3D[],
  lensOnly = false,
): number {
  raycaster.set(origin, dir);
  raycaster.far = far;
  const entries = raycaster.intersectObjects(objects as THREE.Object3D[], true);
  const checked = new Set<string>();
  for (const entry of entries) {
    const key = `${entry.object.id}:${entry.instanceId ?? "mesh"}`;
    if (checked.has(key)) continue;
    checked.add(key);
    raycaster.ray.origin.copy(origin).addScaledVector(dir, far);
    raycaster.ray.direction.copy(dir).negate();
    raycaster.far = far - entry.distance;
    const exit = raycaster.intersectObject(entry.object, false).find(
      (hit: { instanceId?: number }) => hit.instanceId === entry.instanceId,
    );
    if (lensOnly && !exit) {
      // Open track shells have no back face. A missing exit alone must
      // not claim that a distant lens is inside their solid volume.
      const mesh = entry.object as THREE.Mesh;
      const geometry = mesh.geometry;
      if (!geometry) continue;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const world = mesh.matrixWorld.clone();
      if (entry.instanceId !== undefined) {
        const instance = new THREE.Matrix4();
        (mesh as THREE.InstancedMesh).getMatrixAt(entry.instanceId, instance);
        world.multiply(instance);
      }
      const localLens = raycaster.ray.origin.clone().applyMatrix4(
        world.invert(),
      );
      if (!geometry.boundingBox?.containsPoint(localLens)) continue;
    }
    // A missing exit in a containing volume protects the lens; close
    // shots also pull in for substantial foreground obstructions.
    if (
      !exit ||
      (!lensOnly && far - entry.distance - exit.distance >= THIN_SPAN)
    ) {
      return entry.distance;
    }
  }
  return Infinity;
}

/**
 * Occlusion clamp for the follow boom (pure — tests/camera_test.ts).
 * Given the unobstructed boom length and the nearest occluder hit along
 * the look→camera ray (Infinity when clear), returns how long the boom
 * may be. Invariants: never longer than desired, never past the hit
 * (strictly closer than hitDist), and MIN_BOOM only applies when it
 * doesn't violate either.
 */
export function clampBoom(desiredDist: number, hitDist: number): number {
  const target = Math.min(
    desiredDist,
    Math.max(
      hitDist - CLIP_MARGIN,
      Math.min(MIN_BOOM, hitDist - NEAR_CLEARANCE),
    ),
  );
  // Floor: hold the minimum boom, but cap it at half the hit distance so
  // the camera NEVER crosses to the far side of a wall, and at the
  // desired distance so a deliberate user zoom-in is respected.
  return Math.max(target, Math.min(MIN_BOOM, hitDist * 0.5, desiredDist));
}

/**
 * Boom length with asymmetric smoothing (pure — tests/camera_test.ts):
 * snap in to the clamped length when it shrinks, ease back out when it
 * grows. `prevBoom < 0` means uninitialized (first frame / retarget).
 */
export function resolveBoom(
  desiredDist: number,
  hitDist: number,
  prevBoom: number,
  dt: number,
): number {
  const target = clampBoom(desiredDist, hitDist);
  if (!(prevBoom >= 0) || target <= prevBoom) return target; // snap in
  const k = Math.exp(-BOOM_RELEASE_RATE * Math.max(dt, 1e-4));
  return target + (prevBoom - target) * k; // ease out
}

export interface CameraOptions {
  /** Follow-cam trail distance/height — endless mode rides higher and
   * farther back (slalom-scale track pieces would swallow the default). */
  trail?: number;
  height?: number;
  /** Solids for camera clearance. Close shots also avoid foreground
   * occlusion; wide shots preserve framing unless the lens would enter
   * a solid. Resolved per frame — pass a closure over the live scene. */
  occluders?: () => readonly THREE.Object3D[];
  /** Local up (gravity reorientation, M16): the follow cam's up vector,
   * boom height and look offset follow the subject's LOCAL gravity
   * frame — riding a wall the camera rolls with the world. Resolved per
   * frame; smoothed internally. Default: world +y. */
  up?: () => readonly [number, number, number];
}

export function createCameras(
  ctx: SceneCtx,
  curve: THREE.Curve<THREE.Vector3> | null,
  marble: CameraSubject | null,
  opts: CameraOptions = {},
): Cameras {
  const trail = opts.trail ?? TRAIL;
  const height = opts.height ?? HEIGHT;
  const { camera, renderer } = ctx;

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.minDistance = 2;
  controls.maxDistance = 300;

  let mode: CamMode = "orbit";
  let activeCurve = curve;
  let activeMarble = marble;
  let pathSampler: ((s: number, out: THREE.Vector3) => boolean) | null = null;
  /** Last box passed to `frame`, replayed when we return to orbit. */
  let framedBox: THREE.Box3 | null = null;

  const desired = new THREE.Vector3();
  const lookTarget = new THREE.Vector3();
  const orbitTarget = new THREE.Vector3();
  const orbitShift = new THREE.Vector3();
  // Follow-mode user adjust state (wheel zoom, drag orbit).
  let zoomUser = 1;
  let yawUser = 0;
  let pitchUser = 0;
  let lastAdjustT = -1e9;
  const rel = new THREE.Vector3();
  const sph = new THREE.Spherical();
  let dragging = false;
  let dragX = 0;
  let dragY = 0;
  const dom = renderer.domElement;
  dom.addEventListener(
    "wheel",
    (e: WheelEvent) => {
      if (mode !== "follow") return;
      e.preventDefault();
      zoomUser = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, zoomUser * Math.exp(e.deltaY * 0.0012)),
      );
      lastAdjustT = performance.now();
    },
    { passive: false },
  );
  dom.addEventListener("pointerdown", (e: PointerEvent) => {
    if (mode !== "follow") return;
    dragging = true;
    dragX = e.clientX;
    dragY = e.clientY;
  });
  addEventListener("pointermove", (e: PointerEvent) => {
    if (!dragging || mode !== "follow") return;
    yawUser -= (e.clientX - dragX) * 0.006;
    pitchUser += (e.clientY - dragY) * 0.004;
    pitchUser = Math.min(1.1, Math.max(-1.1, pitchUser));
    dragX = e.clientX;
    dragY = e.clientY;
    lastAdjustT = performance.now();
  });
  addEventListener("pointerup", () => {
    dragging = false;
  });
  const lookNow = new THREE.Vector3();
  const subjectPrev = new THREE.Vector3();
  const raycaster = new THREE.Raycaster();
  const rayDir = new THREE.Vector3();
  /** Smoothed boom length (occlusion pull-in state); <0 = uninitialized. */
  let boomNow = -1;
  const bankA = new THREE.Vector3();
  const bankB = new THREE.Vector3();
  const bankC = new THREE.Vector3();
  let bankNow = 0;
  let lookInit = false;
  /** Smoothed local-up (gravity reorientation) — follows opts.up. */
  const upNow = new THREE.Vector3(0, 1, 0);
  const upTarget = new THREE.Vector3(0, 1, 0);

  const api: Cameras = {
    get mode() {
      return mode;
    },

    frame(box: THREE.Box3) {
      if (box.isEmpty()) return;
      framedBox = box.clone();
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) * 0.5 || 8;
      const verticalFov = THREE.MathUtils.degToRad(camera.fov);
      const horizontalFov = 2 * Math.atan(
        Math.tan(verticalFov / 2) * camera.aspect,
      );
      // Portrait windows have a much narrower horizontal frustum. Framing
      // against vertical FOV alone clips wide pieces and their labels.
      const fov = Math.min(verticalFov, horizontalFov);
      const dist = (radius / Math.sin(fov / 2)) * 1.15;
      camera.far = Math.max(500, dist * 4);
      camera.updateProjectionMatrix();
      // Orbit-side effects ONLY in orbit mode. OrbitControls.enabled
      // gates input, not update(): an update() here in follow mode SNAPS
      // the camera to the orbit spherical around the new box center —
      // endless re-frames on every segment build, so every stream load
      // yanked the follow cam to the "default angle" and let the lerp
      // float it back (the user-reported jump; measured as a ~60-unit
      // teleport with a 0.39/100ms decay tail at each build event).
      if (mode === "orbit" && !pathSampler) {
        controls.target.copy(center);
        camera.position.copy(center).add(
          new THREE.Vector3(0.75, 0.55, 0.75).normalize().multiplyScalar(dist),
        );
        camera.lookAt(center);
        controls.update();
      }
    },

    retarget(nextCurve, nextMarble) {
      activeCurve = nextCurve;
      activeMarble = nextMarble;
      pathSampler = null;
      lookInit = false;
      boomNow = -1;
    },

    retargetPath(getPointAtS, nextMarble) {
      pathSampler = getPointAtS;
      activeCurve = null;
      activeMarble = nextMarble;
      lookInit = false;
      boomNow = -1;
    },

    setMode(next: CamMode) {
      if (next === mode) return;
      mode = next;
      controls.enabled = mode === "orbit";
      if (mode === "orbit") {
        // Endless orbit is a chase-orbit: preserve the current boom, but
        // move its pivot with the streamed subject. A fixed world pivot is
        // eventually culled with the old segments, leaving only starfield.
        if (pathSampler && activeMarble) {
          controls.target.copy(activeMarble.obj.position);
          controls.update();
        } else if (framedBox) {
          api.frame(framedBox);
        } else {
          // No box yet — anchor the orbit pivot ahead of the current view.
          camera.getWorldDirection(desired);
          controls.target.copy(camera.position).add(desired.multiplyScalar(12));
        }
      }
      lookInit = false;
      boomNow = -1;
      api.onModeChange?.(mode);
    },

    toggle() {
      api.setMode(mode === "orbit" ? "follow" : "orbit");
      return mode;
    },

    update(dt: number) {
      if (mode === "orbit") {
        if (pathSampler && activeMarble) {
          orbitTarget.copy(activeMarble.obj.position);
          orbitShift.copy(orbitTarget).sub(controls.target);
          camera.position.add(orbitShift);
          controls.target.copy(orbitTarget);
        }
        controls.update();
        return;
      }
      if (!activeMarble) return;
      // Local up: eased toward the supplier (a reorient twist rolls the
      // shot over ~a second, never snaps) except on retarget. The boom
      // height and look offset ride the same axis, so the standard
      // framing survives walls and ceilings unchanged.
      if (opts.up) {
        const u = opts.up();
        upTarget.set(u[0], u[1], u[2]);
        if (!lookInit) upNow.copy(upTarget);
        else {
          upNow.lerp(
            upTarget,
            1 - Math.exp(-2.5 * Math.max(dt, 1e-4)),
          ).normalize();
        }
      }
      camera.up.copy(upNow);
      if (pathSampler) {
        if (!pathSampler(activeMarble.s - trail, desired)) return;
        desired.addScaledVector(upNow, height);
      } else if (activeCurve) {
        const total = activeCurve.getLength();
        if (total <= 0) return;
        const behind = ((activeMarble.s - trail) % total + total) % total;
        activeCurve.getPointAt(behind / total, desired);
        desired.addScaledVector(upNow, height);
      } else {
        return;
      }

      lookTarget.copy(activeMarble.obj.position);
      lookTarget.addScaledVector(upNow, LOOK_UP);

      // Subject teleported (respawn, checkpoint skip): CUT to the fresh
      // framing instead of flying the old camera across the world — the
      // lerp flight dragged the boom through island masses and the
      // anti-clip clamp snapped it mid-route, reading as "camera jumps
      // then floats back" (measured: a 60-unit snap with a decay tail).
      if (lookInit && lookTarget.distanceTo(subjectPrev) > TELEPORT_CUT) {
        lookInit = false;
        boomNow = -1;
        yawUser = 0;
        pitchUser = 0;
      }
      subjectPrev.copy(lookTarget);

      // Drag angles return to the path-facing shot after a quiet spell;
      // zoom is a persistent preference, including across teleport cuts.
      if (
        performance.now() - lastAdjustT > ADJUST_RETURN_AFTER * 1000 &&
        (yawUser !== 0 || pitchUser !== 0)
      ) {
        const k = Math.exp(-ADJUST_RETURN_RATE * Math.max(dt, 1e-4));
        yawUser *= k;
        pitchUser *= k;
        if (Math.abs(yawUser) < 1e-3) yawUser = 0;
        if (Math.abs(pitchUser) < 1e-3) pitchUser = 0;
      }
      if (zoomUser !== 1 || yawUser !== 0 || pitchUser !== 0) {
        rel.copy(desired).sub(lookTarget);
        sph.setFromVector3(rel);
        sph.radius = Math.max(0.6, sph.radius * zoomUser);
        sph.theta += yawUser;
        sph.phi = Math.min(2.6, Math.max(0.25, sph.phi - pitchUser));
        rel.setFromSpherical(sph);
        desired.copy(lookTarget).add(rel);
      }

      // Close shots keep the subject unobstructed. A deliberately wide
      // shot prioritizes the user's framing: scenery crossing the view
      // does not send the camera diving under the track. Still pull clear
      // if the lens itself would enter a solid, including its near plane.
      const wideView = zoomUser > 1.4;
      const obs = opts.occluders ? opts.occluders() : [];
      rayDir.copy(desired).sub(lookTarget);
      const dist = rayDir.length();
      if (obs.length > 0 && dist > 1e-4) {
        rayDir.divideScalar(dist);
        const hit = cameraObstructionHit(
          raycaster,
          lookTarget,
          rayDir,
          dist + CLIP_MARGIN + NEAR_CLEARANCE,
          obs,
          wideView,
        );
        boomNow = resolveBoom(dist, hit, boomNow, dt);
        desired.copy(lookTarget).addScaledVector(rayDir, boomNow);
      } else {
        boomNow = dist;
      }

      // Wide views ease through bends more gently than a close chase shot.
      // Exponential smoothing stays consistent across frame rates.
      const followRate = -60 * Math.log(1 - LERP);
      const wideBlend = Math.min(1, Math.max(0, zoomUser - 1));
      const a = 1 - Math.exp(
        -(followRate * (1 - wideBlend) + 3 * wideBlend) * Math.max(dt, 1e-4),
      );
      camera.position.lerp(desired, a);

      // The lerp above moves the REAL camera on its own path — while it
      // eases toward a freshly pulled-in `desired` (or the boom swings
      // around a corner the desired-ray missed) it can still be inside a
      // mass. Clamp the actual position the same way, along its own ray:
      // snap-in only, the ease-out happens through the lerp next frames.
      if (obs.length > 0) {
        rayDir.copy(camera.position).sub(lookTarget);
        const camDist = rayDir.length();
        if (camDist > 1e-4) {
          rayDir.divideScalar(camDist);
          const hit = cameraObstructionHit(
            raycaster,
            lookTarget,
            rayDir,
            camDist + NEAR_CLEARANCE,
            obs,
            wideView,
          );
          const safe = clampBoom(camDist, hit);
          if (safe < camDist - 1e-6) {
            camera.position.copy(lookTarget).addScaledVector(rayDir, safe);
          }
        }
      }
      if (!lookInit) {
        // Fresh framing (retarget / teleport cut): place the camera at
        // the resolved desired position outright — no cross-world lerp.
        lookNow.copy(lookTarget);
        camera.position.copy(desired);
        lookInit = true;
      } else {
        lookNow.lerp(lookTarget, a);
      }
      camera.lookAt(lookNow);

      // Banking lean (M13): roll into the upcoming curve — signed turn
      // from three path samples around the marble; rotateZ(+φ) banks
      // right, a right turn has (d1×d2).y < 0, so φ = −θ·gain.
      let bankTarget = 0;
      const SPAN = 1.5;
      const s = activeMarble.s;
      const sampled = pathSampler
        ? pathSampler(s - SPAN, bankA) && pathSampler(s, bankB) &&
          pathSampler(s + SPAN, bankC)
        : activeCurve
        ? (() => {
          const total = activeCurve.getLength();
          if (total <= SPAN * 2) return false;
          const at = (ss: number, out: THREE.Vector3) =>
            activeCurve!.getPointAt(
              (((ss % total) + total) % total) / total,
              out,
            );
          at(s - SPAN, bankA);
          at(s, bankB);
          at(s + SPAN, bankC);
          return true;
        })()
        : false;
      if (sampled) {
        const d1x = bankB.x - bankA.x;
        const d1z = bankB.z - bankA.z;
        const d2x = bankC.x - bankB.x;
        const d2z = bankC.z - bankB.z;
        const crossY = d1z * d2x - d1x * d2z;
        const dot = d1x * d2x + d1z * d2z;
        const turn = Math.atan2(crossY, dot);
        bankTarget = Math.max(
          -0.16,
          Math.min(
            0.16,
            -turn * (0.05 + activeMarble.speed * 0.018),
          ),
        );
      }
      bankNow += (bankTarget - bankNow) *
        (1 - Math.pow(1 - 0.05, Math.max(dt, 1e-4) * 60));
      camera.rotateZ(bankNow);
    },

    dispose() {
      controls.dispose();
      removeEventListener("resize", reframe);
    },
  };

  // The old portrait framing was carried into a wide window, leaving
  // the circuit far away after rotation/full screen. Endless orbit is
  // subject-relative and keeps its boom instead of resetting its view.
  const reframe = () => {
    if (mode === "orbit" && framedBox && !pathSampler) api.frame(framedBox);
  };
  addEventListener("resize", reframe);

  addEventListener("keydown", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (
      target?.isContentEditable || target?.closest("input, textarea, select") ||
      ev.repeat || ev.metaKey || ev.ctrlKey || ev.altKey
    ) return;
    if (ev.key === "c" || ev.key === "C") api.toggle();
  });

  return api;
}
